import { IntlShape } from 'react-intl';
import * as yup from 'yup';

import {
  MappingConfig,
  MappingType,
  SupportedMappings,
} from 'apis/merchant-gateway/integrations-middleware-service/types';

import { ValidationContext } from './types';

const validSshPrefixes = [
  'ssh-rsa',
  'ecdsa-sha2-nistp256',
  'ecdsa-sha2-nistp384',
  'ecdsa-sha2-nistp521',
  'ssh-ed25519',
];

export const sshKeyFormSchema = (intl: IntlShape) =>
  yup.object({
    sshKey: yup
      .string()
      .required()
      .test(
        'startsWithHttps',
        intl.formatMessage({ id: 'merchant-integrations.sftp.form.ssh-notice' }),
        (input: string) => input != null && validSshPrefixes.some(prefix => input.startsWith(prefix)),
      ),
  });

const isValidJson = (jsonString: string) => {
  let jsonObject = null;
  if (jsonString != null) {
    try {
      jsonObject = JSON.parse(jsonString);
    } catch (e) {
      // Do nothing for now
      return null;
    }
  }
  return jsonObject;
};

const priceTransformSchema = yup.object({
  type: yup.string().required(),
  value: yup.string().required(),
});

const idFieldMappingSchema = yup.object({
  query: yup.string().required(),
  field_type: yup.mixed().oneOf(['integer', 'double', 'string']),
  transform: priceTransformSchema.default(null).optional().nullable(),
});

const fieldMappingSchema = yup.object({
  query: yup.string().required(),
  field_type: yup.mixed().oneOf(['integer', 'double', 'string', 'boolean']),
  transform: priceTransformSchema.default(null).optional().nullable(),
});

const REQUIRED_IDS = ['sku', 'gtin', 'external_id'];
const atLeastOneIdConfig = {
  name: 'atLeastOneOf',
  message: {
    id: 'validation.atLeastOneOf',
    values: {
      values: REQUIRED_IDS.join(', '),
    },
  },
  exclusive: true,
  params: { path: 'sku|gtin|external_id' },
};

const REQUIRED_ATTRIBS = ['enabled', 'image_url', 'in_stock', 'price', 'vat_percentage'];
const atLeastOneItemUpdateAttribute = {
  name: 'atLeastOneOf',
  message: {
    id: 'validation.atLeastOneOf',
    values: {
      values: REQUIRED_ATTRIBS.join(', '),
    },
  },
  exclusive: true,
  params: { path: 'enabled|image_url|in_stock|price|vat_percentage' },
};

// Inventory update schemas
const inventoryFieldMappingSchema = yup.object({
  query: yup.string().required(),
  field_type: yup.mixed().oneOf(['integer', 'double', 'string']),
  transform: priceTransformSchema.default(null).optional().nullable(),
});

const inventoryUpdateJsonSchema = yup
  .object()
  .shape({
    sku: idFieldMappingSchema.default(null).optional().nullable(),
    external_id: idFieldMappingSchema.default(null).optional().nullable(),
    gtin: idFieldMappingSchema.default(null).optional().nullable(),
    list_field_path: yup.string().required(),
    inventory: inventoryFieldMappingSchema.required(),
  })
  .test({
    ...atLeastOneIdConfig,
    test: value => value == null || REQUIRED_IDS.some(f => value[f] != null),
  });

const inventoryUpdateCsvSchema = yup
  .object()
  .shape({
    sku: idFieldMappingSchema.default(null).optional().nullable(),
    external_id: idFieldMappingSchema.default(null).optional().nullable(),
    gtin: idFieldMappingSchema.default(null).optional().nullable(),
    inventory: inventoryFieldMappingSchema.required(),
    num_of_lines_to_skip: yup.number().default(null).optional().nullable(),
    parser: yup
      .object({
        delimiter: yup.mixed().oneOf(['comma', 'pipe', 'semicolon', 'space', 'tab']),
      })
      .required(),
  })
  .test({
    ...atLeastOneIdConfig,
    test: value => value == null || REQUIRED_IDS.some(f => value[f] != null),
  });

// Item update schemas
const itemUpdateJsonSchema = yup
  .object()
  .shape({
    list_field_path: yup.string().required(),
    sku: idFieldMappingSchema.default(null).optional().nullable(),
    external_id: idFieldMappingSchema.default(null).optional().nullable(),
    gtin: idFieldMappingSchema.default(null).optional().nullable(),
    price: fieldMappingSchema.default(null).optional().nullable(),
    enabled: fieldMappingSchema.default(null).optional().nullable(),
    image_url: fieldMappingSchema.default(null).optional().nullable(),
    in_stock: fieldMappingSchema.default(null).optional().nullable(),
    vat_percentage: fieldMappingSchema.default(null).optional().nullable(),
  })
  .test({
    ...atLeastOneIdConfig,
    test: value => value == null || REQUIRED_IDS.some(f => value[f] != null),
  })
  .test({
    ...atLeastOneItemUpdateAttribute,
    test: value => value == null || REQUIRED_ATTRIBS.some(f => value[f] != null),
  });

const itemUpdateCsvSchema = yup
  .object()
  .shape({
    sku: idFieldMappingSchema.default(null).optional().nullable(),
    external_id: idFieldMappingSchema.default(null).optional().nullable(),
    gtin: idFieldMappingSchema.default(null).optional().nullable(),
    price: fieldMappingSchema.default(null).optional().nullable(),
    enabled: fieldMappingSchema.default(null).optional().nullable(),
    image_url: fieldMappingSchema.default(null).optional().nullable(),
    in_stock: fieldMappingSchema.default(null).optional().nullable(),
    vat_percentage: fieldMappingSchema.default(null).optional().nullable(),
    num_of_lines_to_skip: yup.number(),
    parser: yup.object({
      delimiter: yup.mixed().oneOf(['comma', 'pipe', 'semicolon', 'space', 'tab']),
    }),
  })
  .test({
    ...atLeastOneIdConfig,
    test: value => value == null || REQUIRED_IDS.some(f => value[f] != null),
  })
  .test({
    ...atLeastOneItemUpdateAttribute,
    test: value => value == null || REQUIRED_ATTRIBS.some(f => value[f] != null),
  });

export const configFormSchema = (intl: IntlShape) =>
  yup.object({
    config: yup
      .string()
      .required()
      .test('configFormat', (input: string, testContext: yup.TestContext) => {
        const validJson = isValidJson(input);
        if (!validJson) {
          return testContext.createError({
            message: intl.formatMessage({ id: 'merchant-integrations.mapping-config.json-notice' }),
          });
        }

        const context = testContext.options.context as ValidationContext;
        const mappingConfig = validJson as MappingConfig;
        if (mappingConfig.type !== context.type) {
          return testContext.createError({
            message: intl.formatMessage({ id: 'merchant-integrations.mapping-config.type-notice' }),
          });
        }

        try {
          switch (mappingConfig.type) {
            case MappingType.INVENTORY_UPDATE_CSV:
              return validateMappingSchema(inventoryUpdateCsvSchema, mappingConfig.mapping);
            case MappingType.INVENTORY_UPDATE_JSON:
              return validateMappingSchema(inventoryUpdateJsonSchema, mappingConfig.mapping);
            case MappingType.ITEM_UPDATE_CSV:
              return validateMappingSchema(itemUpdateCsvSchema, mappingConfig.mapping);
            case MappingType.ITEM_UPDATE_JSON:
              return validateMappingSchema(itemUpdateJsonSchema, mappingConfig.mapping);
            default:
              return testContext.createError({ message: 'Please input a valid mapping config' });
          }
        } catch (err) {
          return testContext.createError({
            message: `[mapping.${err.path}]: ${intl.formatMessage(
              { id: err.message.id },
              { values: err.message?.values?.values },
            )}`,
          });
        }
      }),
  });

type ValidMappingSchemas =
  | typeof inventoryUpdateCsvSchema
  | typeof inventoryUpdateJsonSchema
  | typeof itemUpdateCsvSchema
  | typeof itemUpdateJsonSchema;

const validateMappingSchema = (schema: ValidMappingSchemas, mappingConfig: SupportedMappings) => {
  schema.validateSync(mappingConfig, { abortEarly: true });
  return true;
};
